import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Suspense } from "react";
import { MyAcademyArticles } from "./MyAcademyArticles";
import { LoadingPoint } from "@/components/LoadingPoint";
import MyAcademyFiles from "./MyAcademyFiles";
import MyAcademyWorkshopsAccordion from "./MyAcademyWorkshopsAccordion";
import { Link, useNavigate, useLocation } from "@tanstack/react-router";
import { ArrowIcon } from "@/components/icons/arrow";
import { useScrollToTop } from "@/hooks/useScrollToTop";

type MainCategory = "TRAININGS" | "WEBINARS" | "ARTICLES" | "FILES";
type SubCategory = "UPCOMING" | "RECORDED" | "FAVORITES" | "NONE";

export const MyAcademy = (): JSX.Element => {
	const navigate = useNavigate();
	const location = useLocation();
	const { showButton: showScrollToTop, scrollToTop } = useScrollToTop();

	const searchParameters = new URLSearchParams(location.search);
	const mainCategory = ((searchParameters.get("mainCategory") as MainCategory | null) || "TRAININGS");
	const subCategory = ((searchParameters.get("subCategory") as SubCategory | null) || "NONE");

	const handleMainCategoryChange = (category: MainCategory): void => {
		void navigate({
			to: location.pathname,
			search: {
				mainCategory: category,
				subCategory: "NONE",
			},
		});
	};

	const handleSubCategoryChange = (category: SubCategory): void => {
		void navigate({
			to: location.pathname,
			search: {
				mainCategory,
				subCategory: category,
			},
		});
	};

	return (
		<main className="py-3 px-safe-offset-3 min-h-[calc(100vh-176px)] overflow-x-hidden max-w-[890px] mx-auto flex flex-col relative">
			<Tabs className="w-full pb-4" value={mainCategory}>
				<TabsList className="w-full">
					<TabsTrigger
						className="w-1/3 p-2"
						value="TRAININGS"
						onClick={() => {
							handleMainCategoryChange("TRAININGS");
						}}
					>
						Szkolenia
					</TabsTrigger>
					<TabsTrigger
						className="w-1/3 p-2"
						value="WEBINARS"
						onClick={() => {
							handleMainCategoryChange("WEBINARS");
						}}
					>
						Webinary
					</TabsTrigger>
					<TabsTrigger
						className="w-1/3 p-2"
						value="ARTICLES"
						onClick={() => {
							handleMainCategoryChange("ARTICLES");
						}}
					>
						Artykuły
					</TabsTrigger>
					<TabsTrigger
						className="w-1/3 p-2"
						value="FILES"
						onClick={() => {
							handleMainCategoryChange("FILES");
						}}
					>
						Materiały
					</TabsTrigger>
				</TabsList>
			</Tabs>
			{/* {state.subCategory !== "NONE" && (
				<Tabs
					className="w-full pb-4"
					defaultValue="UPCOMING"
					value={state.subCategory}
				>
					<TabsList className="w-full">
						<TabsTrigger
							className="w-1/3 p-2"
							value="UPCOMING"
							onClick={() => {
								dispatch({
									type: "TOGGLE_SUB_CATEGORY",
									payload: "UPCOMING",
								});
							}}
						>
							Nadchodzące
						</TabsTrigger>
						<TabsTrigger
							className="w-1/3 p-2"
							value="RECORDED"
							onClick={() => {
								dispatch({
									type: "TOGGLE_SUB_CATEGORY",
									payload: "RECORDED",
								});
							}}
						>
							Rozpoczęte
						</TabsTrigger>
						<TabsTrigger
							className="w-1/3 p-2"
							value="FAVORITES"
							onClick={() => {
								dispatch({
									type: "TOGGLE_SUB_CATEGORY",
									payload: "FAVORITES",
								});
							}}
						>
							Ulubione
						</TabsTrigger>
					</TabsList>
				</Tabs>
			)} */}
			<div className="flex flex-col gap-3 grow">
				{mainCategory === "ARTICLES" && (
					<Suspense fallback={<LoadingPoint />}>
						<MyAcademyArticles />
					</Suspense>
				)}
				{mainCategory === "FILES" && (
					<Suspense fallback={<LoadingPoint />}>
						<MyAcademyFiles />
					</Suspense>
				)}
				{(mainCategory === "TRAININGS" ||
					mainCategory === "WEBINARS") && (
					<Suspense fallback={<LoadingPoint />}>
						{/* <MyAcademyWorkshops mainCategory={mainCategory} subCategory={subCategory} /> */}
						<MyAcademyWorkshopsAccordion
							mainCategory={mainCategory}
							onSubCategoryChange={handleSubCategoryChange}
						/>
					</Suspense>
				)}
			</div>
			{(mainCategory === "TRAININGS" ||
				mainCategory === "WEBINARS") && (
				<>
					{subCategory !== "NONE" && import.meta.env.VITE_DOWNLOAD_VIDEO_FEATURE_FLAG === "true" && (
						<div className="fixed bottom-[88px] left-[-20px] right-[-20px] px-4 pointer-events-none z-3">
							<Link
								to="/offline-videos"
								search={{
									productType:
										mainCategory === "TRAININGS" ? "training" : "webinar",
								}}
								className="flex items-center justify-center gap-2 bg-primary text-white rounded-none w-full h-12 text-base font-medium shadow-md pointer-events-auto"
							>
								<div className="flex items-center justify-center gap-2">
									<span className="mr-2">Pobrane offline</span>
									<ArrowIcon
										className="rotate-180"
										fillColor="#FFFFFF"
										width={15}
										height={15}
									/>
								</div>
							</Link>
						</div>
					)}
					{subCategory === "NONE" && import.meta.env.VITE_DOWNLOAD_VIDEO_FEATURE_FLAG === "true" && (
						<div className="flex justify-center">
							<Link
								to="/offline-videos"
								search={{
									productType:
										mainCategory === "TRAININGS" ? "training" : "webinar",
								}}
								className="flex items-center justify-center bg-primary text-white rounded-lg w-full max-w-[300px] h-12 text-base font-medium shadow-md pointer-events-auto mb-10"
							>
								<div className="flex items-center justify-center gap-2">
									<span className="mr-2">Pobrane offline</span>
									<ArrowIcon
										className="rotate-180"
										fillColor="#FFFFFF"
										width={15}
										height={15}
									/>
								</div>
							</Link>
						</div>
					)}
					{showScrollToTop && (
						<button
							onClick={scrollToTop}
							className="fixed bottom-[160px] right-4 w-12 h-12 rounded-full bg-primary text-white shadow-lg flex items-center justify-center z-50 hover:bg-primary/90 transition-colors"
							aria-label="Przewiń do góry"
						>
							<ArrowIcon
								className="rotate-90"
								fillColor="#FFFFFF"
								width={20}
								height={20}
							/>
						</button>
					)}
				</>
			)}
		</main>
	);
};
